import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  X,
  Home,
  Compass,
  Search,
  MessageCircle,
  Sparkles,
  Info,
  Users,
  Briefcase,
  Handshake,
  BookMarked,
  User,
  Shield,
  LogOut,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigationStore } from '@/store';
import { useAuth } from '@/providers/auth-provider';
import { usePermissions } from '@/lib/permissions';
import { ROUTES } from '@/utils/constants';
import { useFocusTrap } from '@/hooks';
import { cn, openAskSanjoyWidget } from '@/utils';

const UTILITY_LINKS = [
  { label: 'Home', path: ROUTES.HOME, icon: Home },
  { label: 'Explore', path: ROUTES.EXPLORE, icon: Compass },
  { label: 'Search', path: ROUTES.SEARCH, icon: Search },
] as const;

const SECONDARY_LINKS = [
  { label: 'Capabilities', path: ROUTES.CAPABILITIES, icon: Sparkles },
  { label: 'About', path: ROUTES.ABOUT, icon: Info },
  { label: 'Team', path: ROUTES.TEAM, icon: Users },
  { label: 'Associate Program', path: ROUTES.ASSOCIATE_PROGRAM, icon: Briefcase },
  { label: 'Partners', path: ROUTES.PARTNERS, icon: Handshake },
  { label: 'Resources', path: ROUTES.RESOURCES, icon: BookMarked },
] as const;

export function MobileDrawer() {
  const location = useLocation();
  const { isMobileMenuOpen, closeMobileMenu } = useNavigationStore();
  const { isAuthenticated, signOut, user } = useAuth();
  const { canAccessWorkspace, isAdmin } = usePermissions();
  const focusTrapRef = useFocusTrap(isMobileMenuOpen);

  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname, closeMobileMenu]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  return (
    <AnimatePresence>
      {isMobileMenuOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm lg:hidden"
            onClick={closeMobileMenu}
          />
          <motion.div
            ref={focusTrapRef}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-x-0 bottom-0 z-[201] flex max-h-[85dvh] flex-col rounded-t-[20px] bg-[var(--sj-background)] shadow-2xl lg:hidden pb-safe"
            role="dialog"
            aria-modal="true"
            aria-label="More menu"
          >
            <div className="flex items-center justify-between border-b border-[var(--sj-border)] px-5 py-4">
              <div className="mx-auto h-1 w-10 rounded-full bg-[var(--sj-border)] absolute left-1/2 top-2 -translate-x-1/2" aria-hidden />
              <span className="text-sm font-semibold text-[var(--sj-text-primary)]">More</span>
              <button
                type="button"
                onClick={closeMobileMenu}
                className="touch-target inline-flex items-center justify-center rounded-[8px] text-[var(--sj-text-secondary)] hover:bg-[var(--sj-surface-muted)]"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-3">
              <div className="flex flex-col gap-1">
                {UTILITY_LINKS.map((item) => {
                  const Icon = item.icon;
                  const isActive =
                    item.path === ROUTES.HOME
                      ? location.pathname === ROUTES.HOME
                      : location.pathname === item.path ||
                        location.pathname.startsWith(item.path + '/');
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={closeMobileMenu}
                      className={cn(
                        'flex min-h-11 items-center gap-3 rounded-[10px] px-3 py-2.5 text-sm font-medium touch-manipulation transition-colors',
                        isActive
                          ? 'bg-[var(--sj-surface-muted)] text-[var(--sj-text-primary)]'
                          : 'text-[var(--sj-text-secondary)] active:bg-[var(--sj-surface-muted)]'
                      )}
                    >
                      <Icon className="h-5 w-5 shrink-0" aria-hidden />
                      {item.label}
                    </Link>
                  );
                })}
                <button
                  type="button"
                  onClick={() => {
                    closeMobileMenu();
                    openAskSanjoyWidget();
                  }}
                  className="flex min-h-11 items-center gap-3 rounded-[10px] px-3 py-2.5 text-left text-sm font-medium text-[var(--sj-text-secondary)] touch-manipulation active:bg-[var(--sj-surface-muted)]"
                >
                  <MessageCircle className="h-5 w-5 shrink-0" aria-hidden />
                  Ask Sanjoy
                </button>
              </div>

              <div className="mt-4 border-t border-[var(--sj-border)] pt-3">
                <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-wider text-[var(--sj-text-muted)]">
                  More
                </p>
                <div className="flex flex-col gap-1">
                  {SECONDARY_LINKS.map((item) => {
                    const Icon = item.icon;
                    const isActive =
                      location.pathname === item.path ||
                      location.pathname.startsWith(item.path + '/');
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={closeMobileMenu}
                        className={cn(
                          'flex min-h-11 items-center gap-3 rounded-[10px] px-3 py-2.5 text-sm font-medium touch-manipulation transition-colors',
                          isActive
                            ? 'bg-[var(--sj-surface-muted)] text-[var(--sj-text-primary)]'
                            : 'text-[var(--sj-text-secondary)] active:bg-[var(--sj-surface-muted)]'
                        )}
                      >
                        <Icon className="h-5 w-5 shrink-0" aria-hidden />
                        {item.label}
                      </Link>
                    );
                  })}
                </div>
              </div>

              {isAuthenticated && (
                <div className="mt-4 border-t border-[var(--sj-border)] pt-3">
                  <p className="mb-2 px-3 text-[10px] font-semibold uppercase tracking-wider text-[var(--sj-text-muted)]">
                    Account
                  </p>
                  {user && (
                    <p className="mb-2 truncate px-3 text-xs text-[var(--sj-text-muted)]">
                      {user.email}
                    </p>
                  )}
                  <Link
                    to={ROUTES.PROFILE}
                    onClick={closeMobileMenu}
                    className="flex min-h-11 items-center gap-3 rounded-[10px] px-3 py-2.5 text-sm font-medium text-[var(--sj-text-secondary)] touch-manipulation active:bg-[var(--sj-surface-muted)]"
                  >
                    <User className="h-5 w-5" aria-hidden />
                    Profile
                  </Link>
                  {canAccessWorkspace && (
                    <Link
                      to={isAdmin ? ROUTES.ADMIN : '/admin/knowledge'}
                      onClick={closeMobileMenu}
                      className="flex min-h-11 items-center gap-3 rounded-[10px] px-3 py-2.5 text-sm font-medium text-[var(--sj-text-secondary)] touch-manipulation active:bg-[var(--sj-surface-muted)]"
                    >
                      <Shield className="h-5 w-5" aria-hidden />
                      {isAdmin ? 'Admin' : 'Knowledge Workspace'}
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      closeMobileMenu();
                      void signOut();
                    }}
                    className="flex w-full min-h-11 items-center gap-3 rounded-[10px] px-3 py-2.5 text-left text-sm font-medium text-error-600 touch-manipulation active:bg-error-50"
                  >
                    <LogOut className="h-5 w-5" aria-hidden />
                    Sign Out
                  </button>
                </div>
              )}
            </div>

            {!isAuthenticated && (
              <div className="border-t border-[var(--sj-border)] px-4 py-4">
                <div className="flex justify-center">
                  <Link
                    to={ROUTES.LOGIN}
                    onClick={closeMobileMenu}
                    className="text-sm font-medium text-primary-600 touch-manipulation"
                  >
                    Sign In
                  </Link>
                </div>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
